// @givmo/app-admin — the employer portal's mount of `@givmo/assistant-panel`.
//
// ══ A MOUNT, NOT A FORK (D-EP-44) ══════════════════════════════════════════════════════════════
//
// Everything this file supplies is DATA: the company as the organization the consent instrument
// binds, this portal's words, its three grounded surfaces, and the wire's surface string. The panel
// itself has no idea it is serving an employer, exactly as `@givmo/org-console` has no idea whose
// members it is listing.
//
// ══ WHAT REGISTERS, AND WHEN ═══════════════════════════════════════════════════════════════════
//
// The shell reserves a slot and renders whatever registers into it. `registerEmployerAssistant()`
// registers this component when the switch is on and registers NOTHING when it is off — so a dark
// build's top bar contains no assistant markup of any kind, not a hidden control and not a disabled
// one. `assistant-dark-gate.test.tsx` pins the empty case.
//
// ⚠ The registered component renders BOTH the control and the panel, per the slot's contract. That
// is what lets the panel live in the shell (where it can overlay any page) while its open state
// belongs to the control that opens it.

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyOptions,
} from '@givmo/api-client/query';
import {
  AssistantEntryButton,
  AssistantPanel,
  type AssistantPanelConfig,
} from '@givmo/assistant-panel';
import { registerAssistantEntryPoint } from '../../components/assistant-slot';
import { ROLE_OWNER } from '../admins/admins-roles';
import { PRIVACY_URL } from '../../lib/constants';
import { assistantStreamHeaders } from '../../lib/api-client';
import { isEmployerAssistantEnabled, readAdminEnv } from '../../lib/env';
import {
  ASSISTANT_SURFACES,
  EMPLOYER_WIRE_SURFACE,
  entryContextExtraForPath,
  surfaceIdForPath,
  wireSurfaceForPath,
} from './assistant-surfaces';
import { assistantVocab } from './assistant-vocab';

/** The test-id namespace for every element in this mount. Treat a change like an API change — the
 *  future Playwright spec selects on these. */
const PREFIX = 'assistant';

/**
 * Where THIS admin's first-use notice dismissal is remembered.
 *
 * ⚠ Keyed by the backend user id, not by a bare constant. A shared browser is a real situation in a
 * small company's back office, and a global key would let the first admin's dismissal hide the
 * disclosure from the second one — which is the one failure mode a per-person notice must not have.
 */
function noticeStorageKey(userId: string | null): string | null {
  return userId === null || userId.length === 0 ? null : `givmo.assistant.notice.${userId}`;
}

export function AssistantMount() {
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const me = useQuery(companiesGetCompanyAdminOptions());
  // The company's own name, for the `{org}` slots. It is the SAME read `/settings` makes, so on the
  // common path this is a cache hit rather than a request; elsewhere it costs one GET per staleTime
  // window, which is the price of an acceptance card that names the company being bound rather than
  // saying "your organization" to someone about to sign for it.
  //
  // ⚠ Gated on the panel being OPEN. This component mounts with the shell and lives for the whole
  // session, so an ungated read fires on every app load for a surface nobody has touched. (`me` is
  // NOT gated: the shell already reads it, so it costs nothing and keeps `canAcceptForOrg` right the
  // instant the panel opens.)
  const company = useQuery({ ...companiesGetCompanyOptions(), enabled: open });

  const env = useMemo(() => readAdminEnv(), []);
  const vocab = useMemo(() => assistantVocab(), []);

  const admin = me.data ?? null;
  const orgRef = admin?.admin_company ?? null;
  const userId = admin?.id ?? null;
  // Permissive, matching the config's contract: only a RESOLVED non-Owner is treated as unable to
  // accept, so a slow or failed role read never strands a real Owner behind the "ask an Owner" line.
  const canAcceptForOrg = !(me.isSuccess && admin?.admin_company_role !== ROLE_OWNER);

  const config: AssistantPanelConfig = useMemo(
    () => ({
      orgRef,
      orgName: company.data?.name ?? null,
      canAcceptForOrg,
      wireSurface: EMPLOYER_WIRE_SURFACE,
      // The register the server judges THIS page's replies under (D-EP-47): a fundraiser page stamps
      // the fundraiser surface on each send; everywhere else stamps the thread's own surface.
      pageWireSurface: wireSurfaceForPath(pathname),
      entryContextExtra: entryContextExtraForPath(pathname),
      surfaces: ASSISTANT_SURFACES,
      activeSurfaceId: surfaceIdForPath(pathname),
      vocab,
      privacyPolicyUrl: PRIVACY_URL,
      apiBaseUrl: env.apiBaseUrl,
      authHeaders: assistantStreamHeaders,
      // `DeployEnv` and `GivmoEnv` share their four names, so this is a pass-through rather than a
      // mapping — `local` resolves the dev donate host on both sides.
      env: env.deployEnv,
      // The backend seeds every conversation with a signed CONSUMER greeting that names the assistant
      // as an AI and points at a "Talk to a human" control. On this surface that is both an
      // authorship label D-EP-37 forbids on a reply and a pointer to a control D-EP-38 means this
      // panel not to have — and the disclosure obligation here is carried by the company's own
      // acceptance instrument and the per-admin first-use notice instead. The message is still
      // persisted server-side; it is simply not this surface's disclosure.
      renderServerOpeningMessage: false,
      noticeStorageKey: noticeStorageKey(userId),
      testIdPrefix: PREFIX,
    }),
    [orgRef, company.data?.name, canAcceptForOrg, pathname, vocab, env, userId],
  );

  const handleOpen = useCallback(() => setOpen(true), []);

  return (
    <>
      <AssistantEntryButton
        label={vocab.productName}
        onClick={handleOpen}
        expanded={open}
        testId={`${PREFIX}-entry`}
      />
      <AssistantPanel open={open} onOpenChange={setOpen} config={config} />
    </>
  );
}

/**
 * Register (or, while the switch is off, deliberately do not register) the assistant into the shell.
 *
 * Called once at boot. `source` is injectable so the dark-gate test can prove the OFF case without
 * rebuilding the app under a different env.
 */
export function registerEmployerAssistant(source: ImportMetaEnv = import.meta.env): void {
  registerAssistantEntryPoint(isEmployerAssistantEnabled(source) ? AssistantMount : null);
}
